// The clinic app's build-time environment (Frontend §1.6), validated with zod at startup. Values are trimmed and
// empty counts as unset (Architecture §7). VITE_* values are public: never a secret.
import { z } from 'zod'

/** Trimmed; an empty string counts as unset. */
const blankToUnset = (v: unknown) => (typeof v === 'string' ? v.trim() || undefined : v)
const req = <T extends z.ZodType>(schema: T) => z.preprocess(blankToUnset, schema)
const opt = <T extends z.ZodType>(schema: T) => z.preprocess(blankToUnset, schema.optional())

const origin = z.url({ protocol: /^https?$/ }).transform((u) => u.replace(/\/+$/, ''))
const flag = z.enum(['0', '1']).transform((v) => v === '1')
const hostPort = z.string().regex(/^[\w.-]+:\d{1,5}$/, 'host:port')

export const envSchema = z
  .object({
    VITE_API_ORIGIN: req(origin),
    VITE_API_MOCK: opt(flag).transform((v) => v ?? false),
    VITE_ENV: req(z.enum(['local', 'preview', 'prod'])),
    VITE_FIREBASE_API_KEY: req(z.string()),
    VITE_FIREBASE_AUTH_DOMAIN: req(z.string()),
    VITE_FIREBASE_PROJECT_ID: req(z.string()),
    VITE_FIREBASE_APP_ID: req(z.string()),
    VITE_FIREBASE_AUTH_EMULATOR_HOST: opt(hostPort),
    VITE_ADMIN_APP_ORIGIN: req(origin),
  })
  .superRefine((env, ctx) => {
    if (env.VITE_ENV !== 'prod') return
    if (env.VITE_API_MOCK) {
      ctx.addIssue({
        code: 'custom',
        path: ['VITE_API_MOCK'],
        message: 'VITE_API_MOCK=1 is refused when VITE_ENV=prod',
      })
    }
    if (env.VITE_FIREBASE_AUTH_EMULATOR_HOST !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['VITE_FIREBASE_AUTH_EMULATOR_HOST'],
        message: 'VITE_FIREBASE_AUTH_EMULATOR_HOST is refused when VITE_ENV=prod',
      })
    }
  })

export type StaffEnv = z.output<typeof envSchema>

export type EnvResult = { ok: true; env: StaffEnv } | { ok: false; issues: z.core.$ZodIssue[] }

export function parseEnv(raw: Record<string, unknown>): EnvResult {
  const result = envSchema.safeParse(raw)
  return result.success ? { ok: true, env: result.data } : { ok: false, issues: result.error.issues }
}
